import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { hasModule } from '../session'
import {
  BookOpen,
  BriefcaseBusiness,
  ChartColumn,
  ChevronDown,
  FolderOpen,
  LayoutDashboard,
  Package,
  Receipt,
  Settings,
  ShoppingCart,
  Store,
} from 'lucide-react'

// Sidebar layout, after PropertyBuk and ASYS Tax: Overview up top, folding
// groups under small headings, and set-up pages at the foot. `show` gates a
// link by role and by the plan's modules; `also` lists other path prefixes
// that count as that page.
const NAV = [
  {
    items: [
      { to: '/dashboard', label: 'Overview', Icon: LayoutDashboard, show: (r) => !r.cashierOnly },
    ],
  },
  {
    heading: 'Trading',
    items: [
      { to: '/sales', label: 'Sales', Icon: Store, show: (r) => r.admin || r.sales, modules: ['pos', 'invoices', 'jobs'] },
      { to: '/sales/pos', label: 'Point of sale', Icon: Store, show: (r) => r.cashierOnly, modules: ['pos'] },
      { to: '/jobs', label: 'Jobs', Icon: BriefcaseBusiness, show: (r) => r.admin || r.sales, modules: ['jobs'] },
      { to: '/purchase', label: 'Purchase', Icon: ShoppingCart, show: (r) => r.admin || r.purchase, count: 'po', modules: ['purchase', 'invoices'] }, // Invoices plan: service vendor invoices and payments only
      { to: '/inventory', label: 'Inventory', Icon: Package, show: (r) => r.admin || r.inventory, count: 'inventory', modules: ['inventory'] },
    ],
  },
  {
    heading: 'Money & books',
    items: [
      { to: '/operating', label: 'Expenses', Icon: Receipt, show: (r) => r.admin || r.operating_cost, modules: ['expenses'] },
      { to: '/accounting', label: 'Accounting', Icon: BookOpen, show: (r) => r.admin || r.accounting, modules: ['accounting'] },
      { to: '/accounting/reports', label: 'Reports', Icon: ChartColumn, show: (r) => r.admin || r.accounting, modules: ['accounting'] },
    ],
  },
  {
    foot: true,
    items: [
      { to: '/master', label: 'Master Files', Icon: FolderOpen, also: ['/import'], show: (r) => r.admin || r.sales || r.purchase || r.inventory },
      { to: '/settings', label: 'Settings', Icon: Settings, also: ['/utilities', '/setup', '/onboarding'], show: (r) => r.admin || r.accounting },
    ],
  },
]

const OPEN_GROUPS_KEY = 'asys-retail:nav-open'

function readOpenGroups() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(OPEN_GROUPS_KEY) ?? '[]')
    return Array.isArray(stored) ? stored : []
  } catch {
    return []
  }
}

function storeOpenGroups(value) {
  try {
    window.localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(value))
  } catch {
    // Storage can be blocked (private windows); the sidebar still works.
  }
}

/** Groups start closed again on every sign-in. */
export function closeAllNavGroups() {
  try {
    window.localStorage.removeItem(OPEN_GROUPS_KEY)
  } catch {
    // As above.
  }
}

const startsWith = (path, prefix) => path === prefix || path.startsWith(`${prefix}/`)

/** The link that owns `path`: the longest matching prefix wins, so
    /accounting/reports lights Reports rather than Accounting. */
function activeLinkFor(path, links) {
  let best = null
  let bestLength = -1
  for (const link of links) {
    for (const prefix of [link.to, ...(link.also || [])]) {
      if (startsWith(path, prefix) && prefix.length > bestLength) {
        best = link.to
        bestLength = prefix.length
      }
    }
  }
  return best
}

export default function SideNav({ roles, modules, counts = {}, collapsed, open, onNavigate }) {
  const { pathname } = useLocation()
  const has = {
    admin: roles.includes('admin'),
    sales: roles.includes('sales'),
    purchase: roles.includes('purchase'),
    inventory: roles.includes('inventory'),
    accounting: roles.includes('accounting'),
    operating_cost: roles.includes('operating_cost'),
    // Cashiers reach the POS and nothing else.
    cashierOnly: roles.length > 0 && roles.every((r) => r === 'cashier'),
  }
  const groups = NAV
    .map((group) => ({ ...group, items: group.items.filter((item) => item.show(has) && (!item.modules || item.modules.some((m) => hasModule(modules, m)))) }))
    .filter((group) => group.items.length > 0)
    // A plan with Jobs works from jobs: Jobs comes first under Trading, then Sales.
    .map((group) => (group.heading === 'Trading' && Array.isArray(modules) && modules.includes('jobs')
      ? { ...group, items: [...group.items].sort((a, b) => Number(b.to === '/jobs') - Number(a.to === '/jobs')) }
      : group))
  const activeTo = activeLinkFor(pathname, groups.flatMap((group) => group.items))
  const activeGroup = groups.find((group) => group.heading && group.items.some((item) => item.to === activeTo))?.heading

  // Groups start closed, but the current page's group opens so the page
  // shows in the sidebar; groups opened by hand are remembered on this device.
  const [openGroups, setOpenGroups] = useState(readOpenGroups)
  const [seenGroup, setSeenGroup] = useState(null)
  if (activeGroup !== seenGroup) {
    setSeenGroup(activeGroup)
    if (activeGroup && !openGroups.includes(activeGroup)) setOpenGroups([...openGroups, activeGroup])
  }
  // Icon-only sidebar: the page name appears beside an icon on hover or
  // keyboard focus. Drawn as one fixed label so the sidebar's scrolling
  // doesn't clip it.
  const [tip, setTip] = useState(null)
  const showTip = (event, text) => {
    if (!collapsed) return
    const r = event.currentTarget.getBoundingClientRect()
    setTip({ text, top: r.top + r.height / 2, left: r.right + 10 })
  }
  const hideTip = () => setTip(null)

  const toggleGroup = (heading) => {
    const next = openGroups.includes(heading) ? openGroups.filter((h) => h !== heading) : [...openGroups, heading]
    setOpenGroups(next)
    storeOpenGroups(next)
  }

  return (
    <nav className={`side-nav${open ? ' open' : ''}`} aria-label="Main" onScroll={hideTip}>
      {groups.map((group, index) => {
        const links = group.items.map(({ to, label, Icon, count }) => {
          const n = count ? Number(counts[count] || 0) : 0
          const name = n > 0 ? `${label}, ${n} waiting for approval` : label
          return (
            <Link
              key={to}
              to={to}
              aria-label={collapsed ? name : undefined}
              className={`side-nav-link${to === activeTo ? ' active' : ''}`}
              aria-current={to === activeTo ? 'page' : undefined}
              onClick={(e) => { hideTip(); onNavigate?.(e) }}
              onMouseEnter={(e) => showTip(e, n > 0 ? `${label} · ${n} waiting` : label)}
              onMouseLeave={hideTip}
              onFocus={(e) => showTip(e, n > 0 ? `${label} · ${n} waiting` : label)}
              onBlur={hideTip}
            >
              <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
              <span className="side-nav-label">{label}</span>
              {n > 0 && <span className="nav-count" aria-label={`${n} waiting for approval`}>{n}</span>}
            </Link>
          )
        })
        // A heading over a single link only adds a click.
        if (!group.heading || group.items.length < 2) {
          return <div key={index} className={`nav-group${group.foot ? ' nav-foot' : ''}`}>{links}</div>
        }
        const isOpen = openGroups.includes(group.heading)
        return (
          <div key={index} className="nav-group" role="group" aria-labelledby={`nav-group-${index}`}>
            <button
              type="button"
              className="nav-heading"
              id={`nav-group-${index}`}
              aria-expanded={isOpen}
              aria-controls={`nav-group-${index}-links`}
              onClick={() => toggleGroup(group.heading)}
            >
              {group.heading}
              <ChevronDown className="nav-chevron" size={14} aria-hidden="true" />
            </button>
            <div id={`nav-group-${index}-links`} className={`nav-links${isOpen ? ' is-open' : ''}`}>
              <div className="nav-links-clip">{links}</div>
            </div>
          </div>
        )
      })}
      {collapsed && tip && (
        <span className="side-nav-tip" aria-hidden="true" style={{ top: tip.top, left: tip.left }}>{tip.text}</span>
      )}
    </nav>
  )
}
